import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Rocket, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useActivateCustomers } from "@/hooks/activation";
import {
  clearEmailCampaign,
  type EmailCampaignIdentity,
  getOrCreateEmailCampaign,
  loadPendingEmailCampaign,
} from "@/services/activation/emailCampaignId";
import { ActivationPackWithTarget } from "@/types/activation/activation-pack";

interface ActivateCustomersCardProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_ACTIVATION_RECIPIENTS = 300;

const normalizeRecipientToken = (raw: string): string => {
  const trimmed = raw.trim();
  if (
    trimmed.length >= 2 &&
    ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'")))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  if (trimmed.startsWith("<") && trimmed.endsWith(">")) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
};

// Exported for focused validation of the send-blocking parser.
// eslint-disable-next-line react-refresh/only-export-components
export const parseActivationRecipients = (raw: string) => {
  const valid = new Map<string, string>();
  const invalid: string[] = [];

  raw
    .split(/[\s,;]+/)
    .map(normalizeRecipientToken)
    .filter(Boolean)
    .forEach((value) => {
      const normalized = value.toLowerCase();
      if (EMAIL_PATTERN.test(normalized)) {
        valid.set(normalized, normalized);
        return;
      }
      invalid.push(value);
    });

  return {
    emails: [...valid.values()],
    invalidValues: invalid,
  };
};

const splitDelimitedLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let value = "";
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === delimiter && !quoted) {
      values.push(value.trim());
      value = "";
    } else {
      value += character;
    }
  }

  values.push(value.trim());
  return values;
};

const delimiterCount = (line: string, delimiter: string): number =>
  splitDelimitedLine(line, delimiter).length - 1;

/**
 * Return only values from the CSV's recipient column. Other columns (for
 * example customer names) are not recipients and should not be treated as
 * errors, while every value in the selected email column remains editable so
 * malformed addresses can never disappear silently.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const extractActivationRecipientCsvValues = (raw: string): string[] => {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const delimiters = [",", ";", "\t"];
  const sample = lines.slice(0, 5).join("\n");
  const delimiter = delimiters.reduce((best, candidate) =>
    delimiterCount(sample, candidate) > delimiterCount(sample, best)
      ? candidate
      : best,
  );
  const rows = lines.map((line) => splitDelimitedLine(line, delimiter));
  const normalizedHeaders = rows[0].map((value) =>
    value.toLowerCase().replace(/[^a-z]/g, ""),
  );
  const explicitEmailColumn = normalizedHeaders.findIndex((value) =>
    ["email", "emailaddress", "customeremail", "recipientemail"].includes(
      value,
    ),
  );

  if (explicitEmailColumn >= 0) {
    return rows
      .slice(1)
      .map((row) => row[explicitEmailColumn]?.trim() || "")
      .filter(Boolean);
  }

  const nonEmptyValues = rows.flatMap((row) => row.filter(Boolean));
  if (
    rows.length === 1 ||
    nonEmptyValues.every((value) => value.includes("@"))
  ) {
    return nonEmptyValues;
  }

  const columnCount = Math.max(...rows.map((row) => row.length));
  let likelyEmailColumn = -1;
  let bestScore = 0;
  for (let column = 0; column < columnCount; column += 1) {
    const score = rows.reduce((total, row) => {
      const value = normalizeRecipientToken(row[column] || "").toLowerCase();
      if (EMAIL_PATTERN.test(value)) return total + 3;
      if (value.includes("@")) return total + 1;
      return total;
    }, 0);
    if (score > bestScore) {
      bestScore = score;
      likelyEmailColumn = column;
    }
  }

  if (likelyEmailColumn < 0) {
    // With no identifiable recipient column, retain every value for explicit
    // correction instead of guessing and silently discarding possible typos.
    return nonEmptyValues;
  }

  return rows
    .map((row) => row[likelyEmailColumn]?.trim() || "")
    .filter(Boolean);
};

/**
 * The mass-activation front door: paste or import a customer list, hit one
 * button, and everyone gets one email with the presentation, the app link,
 * and (for agent-backed packs) a working Genie access link — no sign-in, no
 * dashboard access granted. Separate on purpose from the account's actual
 * "team" (the people who manage this Genie) — that lives in Account Settings,
 * linked below rather than duplicated here.
 */
export const ActivateCustomersCard = ({
  pack,
  brand = "#4E9CFF",
}: ActivateCustomersCardProps) => {
  const activateCustomers = useActivateCustomers(pack);
  const [input, setInput] = useState("");
  const [lastResult, setLastResult] = useState<{
    sent: number;
    notSent: number;
    unknown: number;
  } | null>(null);
  const [isRestoringCampaign, setIsRestoringCampaign] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const campaignRef = useRef<EmailCampaignIdentity | null>(null);

  const { emails, invalidValues } = parseActivationRecipients(input);
  const hasInvalidRecipients = invalidValues.length > 0;
  const exceedsRecipientLimit = emails.length > MAX_ACTIVATION_RECIPIENTS;
  const campaignScope = `customer-activation:${pack.id}`;

  useEffect(() => {
    let active = true;
    setIsRestoringCampaign(true);
    void loadPendingEmailCampaign(campaignScope)
      .then((campaign) => {
        if (!active || !campaign) return;
        campaignRef.current = campaign;
        setInput(campaign.recipients.join(", "));
      })
      .finally(() => {
        if (active) setIsRestoringCampaign(false);
      });
    return () => {
      active = false;
    };
  }, [campaignScope]);

  const handleCsvImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const importedValues = extractActivationRecipientCsvValues(text);
      if (importedValues.length === 0) {
        toast.error("That file doesn't contain any recipient values");
        return;
      }
      const imported = parseActivationRecipients(importedValues.join("\n"));
      setInput(
        [input.trim(), importedValues.join(", ")].filter(Boolean).join(", "),
      );
      setLastResult(null);
      if (imported.invalidValues.length > 0) {
        toast.warning(
          `Imported ${imported.emails.length} valid email${imported.emails.length === 1 ? "" : "s"}; fix ${imported.invalidValues.length} invalid ${imported.invalidValues.length === 1 ? "entry" : "entries"} before activating`,
        );
      } else {
        toast.success(
          `Imported ${imported.emails.length} email${imported.emails.length === 1 ? "" : "s"}`,
        );
      }
    };
    reader.readAsText(file);
  };

  const handleActivate = async () => {
    if (
      emails.length === 0 ||
      hasInvalidRecipients ||
      exceedsRecipientLimit
    )
      return;
    const fingerprint = [campaignScope, ...[...emails].sort()].join("\n");
    if (
      campaignRef.current &&
      campaignRef.current.fingerprint !== fingerprint
    ) {
      const replace = globalThis.confirm(
        "A previous customer campaign is still pending. Replacing it may duplicate recipients whose delivery could not be confirmed. Start a new campaign with this edited list?",
      );
      if (!replace) return;
      clearEmailCampaign(campaignRef.current);
      campaignRef.current = null;
    }
    if (campaignRef.current?.fingerprint !== fingerprint) {
      campaignRef.current = await getOrCreateEmailCampaign(
        campaignScope,
        emails,
      );
    }
    const campaign = campaignRef.current;
    activateCustomers.mutate(
      { emails, campaignId: campaign.id },
      {
        onSuccess: (result) => {
          setLastResult(result);
          if (result.notSent === 0 && result.unknown === 0) {
            toast.success(
              `Sent to ${result.sent} customer${result.sent === 1 ? "" : "s"}`,
            );
            setInput("");
            clearEmailCampaign(campaign);
            if (campaignRef.current?.id === campaign.id)
              campaignRef.current = null;
            setLastResult(null);
            return;
          }
          if (result.unknown > 0) {
            toast.warning(
              `${result.sent} sent; ${result.unknown} delivery result${result.unknown === 1 ? " is" : "s are"} unknown and will not be re-sent automatically`,
            );
          } else {
            toast.error(
              `${result.notSent} email${result.notSent === 1 ? "" : "s"} could not be sent. Retry to send only those known not sent.`,
            );
          }
        },
        onError: () =>
          toast.error(
            "Couldn't confirm the campaign. Retry safely to resume it without re-sending completed emails.",
          ),
      },
    );
  };

  const handleStartNewCampaign = () => {
    if (
      lastResult?.unknown &&
      !globalThis.confirm(
        "Some delivery results are unknown. Starting a new campaign may send those customers a duplicate email. Continue?",
      )
    )
      return;
    if (campaignRef.current) clearEmailCampaign(campaignRef.current);
    campaignRef.current = null;
    setLastResult(null);
    toast.info("Ready to start a new campaign");
  };

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] mb-4 lg:mb-6">
      <div className="rounded-[14px] bg-white p-6 md:p-8">
        <div className="text-center mb-5">
          <h2 className="text-2xl md:text-3xl font-bold text-[#222B45]">
            Activate your customers
          </h2>
          <p className="text-[#64748B] mt-1">
            Paste your list — or import a CSV — and everyone gets one email with
            the presentation, the app link
            {pack.agent_id ? ", and a link straight into your Genie" : ""}. No
            sign-in required.
          </p>
        </div>

        <div className="max-w-xl mx-auto space-y-3">
          <Textarea
            value={input}
            disabled={isRestoringCampaign}
            aria-invalid={hasInvalidRecipients}
            aria-describedby={
              hasInvalidRecipients ? "activation-recipient-errors" : undefined
            }
            onChange={(e) => {
              setInput(e.target.value);
              setLastResult(null);
            }}
            placeholder="Paste emails separated by commas — or import a CSV"
            className="min-h-[88px] resize-y text-center"
            rows={3}
          />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              className="hidden"
              onChange={handleCsvImport}
            />
            <Button
              type="button"
              variant="outline"
              disabled={isRestoringCampaign}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={handleActivate}
              disabled={
                emails.length === 0 ||
                hasInvalidRecipients ||
                exceedsRecipientLimit ||
                isRestoringCampaign ||
                activateCustomers.isPending ||
                (lastResult !== null && lastResult.notSent === 0)
              }
              size="lg"
              className="text-white border-0 px-8"
              style={{
                background: `linear-gradient(135deg, ${brand}, #FF4DB8)`,
              }}
            >
              {activateCustomers.isPending ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Rocket className="w-5 h-5 mr-2" />
              )}
              {activateCustomers.isPending
                ? "Activating…"
                : lastResult?.notSent
                  ? `Retry known unsent (${lastResult.notSent})`
                  : `Activate customers${emails.length ? ` (${emails.length})` : ""}`}
            </Button>
          </div>
          {hasInvalidRecipients && (
            <div
              id="activation-recipient-errors"
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left"
            >
              <p className="text-sm font-semibold text-red-600">
                Fix or remove {invalidValues.length} invalid recipient
                {invalidValues.length === 1 ? "" : "s"} before activating.
              </p>
              <p className="mt-1 text-xs text-red-600 break-words">
                {invalidValues.slice(0, 6).join(", ")}
                {invalidValues.length > 6
                  ? `, plus ${invalidValues.length - 6} more`
                  : ""}
              </p>
            </div>
          )}
          {lastResult && (lastResult.notSent > 0 || lastResult.unknown > 0) && (
            <div className="text-center space-y-1.5">
              <p className="text-sm text-amber-700">
                {lastResult.sent} sent.{" "}
                {lastResult.notSent > 0 &&
                  `${lastResult.notSent} known not sent. `}
                {lastResult.unknown > 0 &&
                  `${lastResult.unknown} awaiting manual delivery confirmation — they are locked against duplicate sends.`}
              </p>
              {lastResult.unknown > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleStartNewCampaign}
                >
                  Start a new campaign (may duplicate)
                </Button>
              )}
            </div>
          )}
          {exceedsRecipientLimit && (
            <p className="text-center text-sm font-medium text-red-600">
              Send to at most {MAX_ACTIVATION_RECIPIENTS} customers at a time.
            </p>
          )}
          <p className="text-center text-xs text-gray-400">
            Need to add someone to your HelpGenie team instead?{" "}
            <Link
              to="/genie-hub/account-settings"
              className="underline hover:text-gray-600"
            >
              Manage team members
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
